import React from 'react';
import { observer } from 'mobx-react';

import { Link } from 'ui/base/link';
import { BackButton } from 'ui/base/button/back_button';
import { GlobalThemeStore, Theme, ThemedStyles, useStyles } from 'ui/base/theme';
import { StandardPage } from 'ui/base/page/standard_page';

import { Avatar } from 'ui/standard/avatar/avatar';
import { SwitchInput } from 'ui/standard/input/switch_input';
import { LocaleSelector } from 'ui/standard/search/locale_selector/locale_selector';

import { APP_VERSION } from 'services/base/config';
import { BasePath } from 'services/base/page_path';
import { GlobalSettingsStore } from 'services/base/app_settings';

import { User } from 'services/accounts/types';
import { useAccountsDomain } from 'services/accounts/accounts_domain';

import { Redirect } from 'pages/core/redirect/redirect';

import BaseStyles from './settings.module.css';
import Messages from './settings_messages';
import { SettingsStore } from './settings_store';

type SettingsStyles = {
  baseRow: string;
  column: string;
  email: string;
  profilePhoto: string;
  link: string;
  actionsContainer: string;
  actionRow: string;
  subText: string;
  version: string;
};

const themedStyles: ThemedStyles<SettingsStyles> = {
  [Theme.DAYLIGHT]: {
    baseRow: BaseStyles.baseRow,
    column: BaseStyles.column,
    email: BaseStyles.email,
    profilePhoto: BaseStyles.profilePhoto,
    link: BaseStyles.daylightLink,
    actionsContainer: BaseStyles.actionsContainer,
    actionRow: BaseStyles.actionRow,
    subText: BaseStyles.daylightSubText,
    version: BaseStyles.daylightVersionText,
  },
  [Theme.MIDNIGHT]: {
    baseRow: BaseStyles.baseRow,
    column: BaseStyles.column,
    email: BaseStyles.email,
    profilePhoto: BaseStyles.profilePhoto,
    link: BaseStyles.midnightLink,
    actionsContainer: BaseStyles.actionsContainer,
    actionRow: BaseStyles.actionRow,
    subText: BaseStyles.midnightSubText,
    version: BaseStyles.midnightVersionText,
  },
};

type BaseSettingsProps = {
  user: User;
  store: SettingsStore;
};

export const BaseSettings = observer(({ user, store }: BaseSettingsProps) => {
  const styles = useStyles(themedStyles);
  return (
    <StandardPage>
      <BackButton/>
      <Link path={{ kind: BasePath.SETTINGS_EDIT }} className={styles.link}>
        <div className={styles.baseRow}>
          <div className={styles.column}>
            <div className={styles.email}>{user.email}</div>
            <div className={styles.subText}>{Messages.viewAndEdit()}</div>
          </div>
          <Avatar avatarCode={user.avatarCode}/>
        </div>
      </Link>
      <div className={styles.actionsContainer}>
        <div className={styles.actionRow}>
          <div>{Messages.pushNotifications()}</div>
          <div>
            <SwitchInput store={store.pushNotification} id='push-notification' name='push-notification'/>
          </div>
        </div>
        <div className={styles.actionRow}>
          <div>{Messages.nightMode()}</div>
          <div>
            <SwitchInput store={store.nightMode} id='night-mode' name='night-mode'/>
          </div>
        </div>
        <div className={styles.actionRow}>
          <div>{Messages.hideOfflineDevices()}</div>
          <div>
            <SwitchInput store={store.hideOfflineDevices} id='hide-offline-devices' name='hide-offline-devices'/>
          </div>
        </div>
        <div className={styles.actionRow}>
          <div>{Messages.language()}</div>
          <div>
            <LocaleSelector/>
          </div>
        </div>
      </div>
      <div className={styles.baseRow}>
        <div>{Messages.appVersion()}</div>
        <div className={styles.version}>{APP_VERSION}</div>
      </div>
    </StandardPage>
  );
});

export const Settings = observer(() => {
  const accounts = useAccountsDomain();

  const user = accounts.user;
  if (!user) return <Redirect />;

  const [store] = React.useState(new SettingsStore({
    domain: accounts,
    pushNotification: user.pushNotifications,
    nightMode: GlobalThemeStore.get() === Theme.MIDNIGHT,
    hideOfflineDevices: GlobalSettingsStore.get().hideOfflineDevices,
  }));

  return (
    <BaseSettings
      user={user}
      store={store}
    />
  );
});
